import { useTranslator } from "@eo-locale/react";

import { number, object, string } from "yup";

import {
  FormLabel,
  FormControl,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  FileUpload,
  VisuallyHidden
} from "@/storybook-components";
import type { ChangeEvent } from "react";
import { fileUpload } from "@/models";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { setAnswer, setError } from "@/redux/features/questions/slice";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

interface IFileQuestion {
  question: Question;
}

export type FileAnswer = {
  fileUrl: string;
  fileName: string;
  fileSize: number;
  fileContentType: string;
};

export const validate = async (
  answer: FileAnswer,
  question: Question
): Promise<string[]> => {
  const shape: any = {
    fileName: string(),
    fileSize: number(),
    fileContentType: string()
  };
  if (question.required) {
    shape.fileUrl = string().required();
  }
  const schema = object(shape);
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const MAX_FILE_SIZE = 200000000;

const FileQuestion = ({ question }: IFileQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { errors, answers, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleChange = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      dispatch(
        setError({
          questionId: question.id,
          error: "tooBigFile"
        })
      );
      return;
    }

    try {
      const file = await fileUpload(token, baseUrl, selectedFile);

      dispatch(
        setAnswer({
          questionId: question.id,
          answer: {
            url: file.url,
            file_name: selectedFile.name,
            file_size: selectedFile.size,
            file_content_type: selectedFile.type
          }
        })
      );
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      dispatch(
        setError({
          questionId: question.id,
          error: "uploadError"
        })
      );
    }
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <FileUpload
        fileName={answer?.file_name}
        onUpload={handleChange}
        aria-label={
          answer?.url
            ? translate("common.changeFile")
            : translate("common.chooseFile")
        }
        size={{ base: "lg", lg: "md" }}
      >
        {answer?.url
          ? translate("common.changeFile")
          : translate("common.chooseFile")}
      </FileUpload>
      {error.length > 0 && (
        <FormErrorMessage>
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default FileQuestion;
